import React from 'react';
import { Box, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslate } from 'react-admin';
import { PeriodRange } from '../../../utils/dashboard/periodRange';
import { CRM_COLORS } from '../../../theme/crmColors';

const useStyles = makeStyles(() => ({
    row: {
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
    },
    field: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 150,
    },
    label: {
        fontWeight: 500,
        fontSize: '0.75rem',
        color: CRM_COLORS.text.secondary,
        marginBottom: 4,
    },
    input: {
        fontSize: '0.85rem',
    },
}));

interface PeriodRangeFilterProps {
    value: PeriodRange;
    onChange: (range: PeriodRange) => void;
}

/**
 * El tema global (`customTheme.js`) fuerza
 * `MuiInputLabel.formControl { position: inherit !important }`. La variante
 * `outlined` de MUI necesita `position: absolute` para encajar el label en el
 * hueco del borde, así que con ese override el label cae al flujo normal y se
 * pinta descolocado encima del campo.
 *
 * Por eso la etiqueta va aparte, en un `Typography`, y el `TextField` no recibe
 * la prop `label`. Es el mismo patrón que ya usa `FilterField.tsx`.
 */
const PeriodRangeFilter: React.FC<PeriodRangeFilterProps> = ({ value, onChange }) => {
    const classes = useStyles();
    const translate = useTranslate();

    const fromLabel = translate('app.dashboard.dateFrom');
    const toLabel = translate('app.dashboard.dateTo');

    return (
        <Box className={classes.row}>
            <Box className={classes.field}>
                <Typography className={classes.label}>{fromLabel}</Typography>
                <TextField
                    type='date'
                    size='small'
                    variant='outlined'
                    inputProps={{ 'data-testid': 'period-from', 'aria-label': fromLabel }}
                    InputProps={{ className: classes.input }}
                    value={value.from}
                    onChange={(event) => onChange({ ...value, from: event.target.value })}
                />
            </Box>

            <Box className={classes.field}>
                <Typography className={classes.label}>{toLabel}</Typography>
                <TextField
                    type='date'
                    size='small'
                    variant='outlined'
                    inputProps={{ 'data-testid': 'period-to', 'aria-label': toLabel }}
                    InputProps={{ className: classes.input }}
                    value={value.to}
                    onChange={(event) => onChange({ ...value, to: event.target.value })}
                />
            </Box>
        </Box>
    );
};

export default PeriodRangeFilter;
